import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { followOfficeMutation } from "@/components/model/office/_graphql";
import {
  Badge,
  Button,
  Card,
  Center,
  CheckIcon,
  ChevronRightIcon,
  Group,
  HeartIcon,
  MapPinIcon,
  Rating,
  Stack,
  Text,
} from "@/components/ui/elements";
import { RatingField, TextAreaField } from "@/components/ui/parts";

import { infoMessage } from "@/i18n";
import { showNotification } from "@/util/userNotifications";

import { reviewTemporaryJobOfferMutation } from "../_graphql";
import { reviewJobOfferInfoQuery } from "./_graphql";
import { MapLocationReportModal } from "./MapLocationReportModal";

const WANT_TO_WORK_AGAIN_PINK = "var(--mantine-color-pink-6)";

const schema = z.object({
  healthRate: z.number(),
  atmosphereRate: z.number(),
  beginOnTimeRate: z.number(),
  differenceInjobOfferRate: z.number(),
  comment: z
    .string()
    .max(200, "コメントは200文字以内で入力してください")
    .refine((value) => value.trim().length > 0, {
      message: "コメントを入力してください",
    }),
});

type Schema = z.infer<typeof schema>;

// コメントテンプレート
const COMMENT_TEMPLATES = [
  "ありがとうございました。\nまた機会がありましたらよろしくお願いいたします。",
  "本日はお世話になりました。\n良い雰囲気で働かせていただき、ありがとうございました。",
  "ご利用者様やスタッフの方々に温かく迎えていただき、感謝しております。\nまたお仕事の機会をいただければ幸いです。",
  "充実した時間を過ごさせていただきました。\nスタッフの方々のサポートもありがたかったです。",
  "本日はありがとうございました。\n施設の雰囲気も良く、働きやすかったです。\nまたの機会を楽しみにしております。",
];

// ランダムにコメントを選択（抽選から現在のコメントを除外）
const getRandomComment = (currentComment?: string) => {
  const availableComments = currentComment
    ? COMMENT_TEMPLATES.filter((comment) => comment !== currentComment)
    : COMMENT_TEMPLATES;

  // フィルター後の配列が空の場合は元の配列から選ぶ（念のため）
  if (availableComments.length === 0) {
    return COMMENT_TEMPLATES[
      Math.floor(Math.random() * COMMENT_TEMPLATES.length)
    ];
  }

  return availableComments[
    Math.floor(Math.random() * availableComments.length)
  ];
};

type Props = {
  close: () => void;
  temporaryJobOfferId: string;
  onReviewComplete: (average: number) => void;
  formatWorkDate: string;
  officeName: string;
  // 地図・場所の報告モーダルの開閉。重ねて開いている間は親モーダルを Esc で閉じないようにするために通知する
  onMapLocationReportOpenedChange?: (opened: boolean) => void;
};

export function Review(props: Props) {
  const {
    close,
    temporaryJobOfferId,
    onReviewComplete,
    formatWorkDate,
    officeName,
    onMapLocationReportOpenedChange,
  } = props;

  // 初期コメントをランダムに生成（メモ化して再レンダリング時に変更されないようにする）
  const initialComment = useMemo(() => getRandomComment(), []);

  const { control, watch, handleSubmit, setValue } = useForm<Schema>({
    defaultValues: {
      healthRate: 5,
      atmosphereRate: 5,
      beginOnTimeRate: 5,
      differenceInjobOfferRate: 5,
      comment: initialComment,
    },
    resolver: zodResolver(schema),
  });

  const values = watch([
    "healthRate",
    "atmosphereRate",
    "beginOnTimeRate",
    "differenceInjobOfferRate",
  ]);
  const currentComment = watch("comment");

  // ランダムコメント生成ハンドラー（現在のコメントを除外）
  const handleRandomComment = () => {
    const randomComment = getRandomComment(currentComment);
    setValue("comment", randomComment);
  };
  const [mutation, { loading }] = useMutation(reviewTemporaryJobOfferMutation, {
    refetchQueries: ["ReviewRequests", "MyPage", "UnreviewedCount"],
  });

  const {
    data: jobOfferInfoData,
    loading: jobOfferInfoLoading,
    refetch: refetchJobOfferInfo,
  } = useQuery(reviewJobOfferInfoQuery, { variables: { temporaryJobOfferId } });

  const [followOffice, { loading: followOfficeLoading }] = useMutation(
    followOfficeMutation,
    {
      refetchQueries: [
        "Bookmarks",
        "JobOfferFeed",
        "JobOffer",
        "OfficeJobOfferList",
        "ReviewRequests",
      ],
    },
  );

  const [didFollowFromReview, setDidFollowFromReview] = useState(false);
  const [mapLocationReportOpened, setMapLocationReportOpened] = useState(false);
  const changeMapLocationReportOpened = (opened: boolean) => {
    setMapLocationReportOpened(opened);
    onMapLocationReportOpenedChange?.(opened);
  };

  // 送信に成功すると開いたボタンが送信済み表示に置き換わり、フォーカスの戻り先がなくなるため、送信済み表示へ移す
  const mapLocationReportedRef = useRef<HTMLDivElement>(null);
  const [focusMapLocationReported, setFocusMapLocationReported] =
    useState(false);
  const mapLocationReported =
    jobOfferInfoData?.temporaryJobOffer?.mapLocationReported;
  useEffect(() => {
    if (!focusMapLocationReported || mapLocationReportOpened) return;
    if (!mapLocationReported || !mapLocationReportedRef.current) return;
    mapLocationReportedRef.current.focus();
    setFocusMapLocationReported(false);
  }, [focusMapLocationReported, mapLocationReportOpened, mapLocationReported]);

  const jobOfferInfo = jobOfferInfoData?.temporaryJobOffer;
  const showWantToWorkAgain =
    !jobOfferInfoLoading &&
    jobOfferInfo &&
    !jobOfferInfo.officeFollowed &&
    !didFollowFromReview;

  const handleWantToWorkAgain = async () => {
    if (!jobOfferInfo) return;
    const clientOfficeId = jobOfferInfo.postedClientOffice.clientOfficeId;
    await followOffice({
      variables: {
        input: {
          clientOfficeId,
        },
      },
      onError: (error) =>
        showNotification({
          type: "error",
          message: error.message,
        }),
      onCompleted: (res) => {
        if (!res.followOffice) return;
        setDidFollowFromReview(true);
        showNotification({
          type: "info",
          message: infoMessage("addOfficeFavorite"),
        });
      },
    });
  };

  const average = values.reduce((acc, num) => acc + num, 0) / values.length;
  const onSubmit = async (data: Schema) => {
    await mutation({
      variables: {
        input: {
          temporaryJobOfferId: temporaryJobOfferId,
          atmosphereRate: data.atmosphereRate,
          beginOnTimeRate: data.beginOnTimeRate,
          differenceInJobOfferRate: data.differenceInjobOfferRate,
          healthRate: data.healthRate,
          comment: data.comment,
          // 地図・場所の報告はレビューと独立して MapLocationReportModal から送信するため、ここでは送らない
        },
      },
      onError: (error) =>
        showNotification({
          type: "error",
          message: error.message,
        }),
      onCompleted: (data) => {
        const res = data.reviewClientOffice;
        if (!res) return;
        showNotification({
          type: "info",
          message: infoMessage("addReview"),
        });
        onReviewComplete(average);
      },
    });
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="sm">
          <Card mx="xs" p="sm">
            <Stack gap={4}>
              <Text fz="sm" fw="bold">
                勤務日時：{formatWorkDate}
              </Text>
              <Text fz="sm" fw="bold">
                施設名：{officeName}
              </Text>
            </Stack>
          </Card>
          <Card mx="xs" p="sm">
            <Stack gap="xs">
              <div>
                <Text fz="sm" mb={4}>
                  綺麗さ・衛生面はよかったか
                </Text>
                <Group align="center" gap="xs">
                  <Text fw="bold" fz="md">
                    {values[0].toFixed(1)}
                  </Text>
                  <RatingField
                    fractions={2}
                    size="md"
                    control={control}
                    name="healthRate"
                  />
                </Group>
              </div>
              <div>
                <Text fz="sm" mb={4}>
                  施設の雰囲気はよかったか
                </Text>
                <Group align="center" gap="xs">
                  <Text fw="bold" fz="md">
                    {values[1].toFixed(1)}
                  </Text>
                  <RatingField
                    fractions={2}
                    size="md"
                    control={control}
                    name="atmosphereRate"
                  />
                </Group>
              </div>
              <div>
                <Text fz="sm" mb={4}>
                  予定時間通りに業務が開始されたか
                </Text>
                <Group align="center" gap="xs">
                  <Text fw="bold" fz="md">
                    {values[2].toFixed(1)}
                  </Text>
                  <RatingField
                    fractions={2}
                    size="md"
                    control={control}
                    name="beginOnTimeRate"
                  />
                </Group>
              </div>
              <div>
                <Text fz="sm" mb={4}>
                  求人内容と実際の業務に差異はなかったか
                </Text>
                <Group align="center" gap="xs">
                  <Text fw="bold" fz="md">
                    {values[3].toFixed(1)}
                  </Text>
                  <RatingField
                    fractions={2}
                    size="md"
                    control={control}
                    name="differenceInjobOfferRate"
                  />
                </Group>
              </div>
            </Stack>
          </Card>
          <Center>
            <Group gap="xs" align="center">
              <Text variant="center" fz="md" fw="bold">
                総合評価：{average.toFixed(2)}
              </Text>
              <Rating size="lg" value={average} readOnly fractions={4} />
            </Group>
          </Center>
          <Card mx="xs" p="sm">
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Group gap={6} align="center">
                  <Text fz="sm" fw={500}>
                    コメント入力
                  </Text>
                  <Badge
                    size="sm"
                    color="red"
                    style={{ textTransform: "none" }}
                  >
                    必須
                  </Badge>
                </Group>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleRandomComment}
                >
                  コメント例を選ぶ
                </Button>
              </Group>
              <TextAreaField
                description="評価コメントを入力してください。　※コメントは編集できます。"
                name="comment"
                rows={4}
                control={control}
              />
            </Stack>
          </Card>
          {/* 再取得中もキャッシュがあれば表示し続け、初回読み込み中のみ出さない */}
          {!jobOfferInfo && jobOfferInfoLoading ? null : (
            <Card mx="xs" p="sm">
              {jobOfferInfo?.mapLocationReported ? (
                <div ref={mapLocationReportedRef} tabIndex={-1}>
                  <Group gap={6} wrap="nowrap">
                    <CheckIcon
                      color="var(--mantine-color-teal-6)"
                      size="1.1em"
                    />
                    <Text fz="sm" c="teal.7">
                      地図・場所の報告は送信済みです
                    </Text>
                  </Group>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="subtle"
                  color="teal"
                  size="compact-sm"
                  px={0}
                  onClick={() => changeMapLocationReportOpened(true)}
                  leftSection={<MapPinIcon size="1.1em" />}
                  rightSection={<ChevronRightIcon size="1em" />}
                  // スマホ幅でも文言が途中で切れないよう、ボタン内で折り返す
                  styles={{
                    root: {
                      height: "auto",
                      minHeight: "var(--button-height)",
                      paddingBlock: 4,
                    },
                    label: {
                      whiteSpace: "normal",
                      textAlign: "left",
                      lineHeight: 1.4,
                    },
                  }}
                >
                  地図・場所の表示が実際と違った場合はこちら
                </Button>
              )}
            </Card>
          )}
          {showWantToWorkAgain && jobOfferInfo ? (
            <Card mx="xs" p="sm">
              <Stack gap="xs" align="stretch">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  fullWidth
                  loading={followOfficeLoading}
                  onClick={handleWantToWorkAgain}
                  leftSection={
                    <HeartIcon color={WANT_TO_WORK_AGAIN_PINK} size="1.15em" />
                  }
                  styles={{
                    root: {
                      color: WANT_TO_WORK_AGAIN_PINK,
                      borderColor: WANT_TO_WORK_AGAIN_PINK,
                    },
                  }}
                >
                  また働きたい
                </Button>
                <Text
                  fz="xs"
                  ta="center"
                  style={{ color: WANT_TO_WORK_AGAIN_PINK }}
                >
                  {`タップすると「${jobOfferInfo.postedClientOffice.name}」をフォローします`}
                </Text>
              </Stack>
            </Card>
          ) : null}
          <Card mx="xs" p="sm">
            <Stack gap={4}>
              <Text fz="xs" c="dimmed">
                ※
                求人内容と業務内容に大きな差異があった場合や、ハラスメント行為等があった場合はケアリンク管理者へ報告お願いします。
              </Text>
              <Group>
                <Button
                  component={Link}
                  href="/message/admin_message"
                  variant="outline"
                  size="xs"
                >
                  管理者へのメッセージ
                </Button>
              </Group>
            </Stack>
          </Card>
          <Group justify="center" gap="xs">
            <Button variant="white" onClick={close} size="sm">
              閉じる
            </Button>
            <Button loading={loading} type="submit" size="sm">
              レビュー送信
            </Button>
          </Group>
        </Stack>
      </form>
      {/* React のイベントは Portal 越しに親へ伝播するため、レビューの form の外に置く */}
      <MapLocationReportModal
        opened={mapLocationReportOpened}
        close={() => changeMapLocationReportOpened(false)}
        temporaryJobOfferId={temporaryJobOfferId}
        onSendError={() => refetchJobOfferInfo()}
        onReported={() => setFocusMapLocationReported(true)}
      />
    </>
  );
}
